import { type ProspectDto } from "@crowdcoursing/api/openapi";
import { Language, LocationOn, Source } from "@mui/icons-material";
import { Avatar, Box, Paper, Typography } from "@mui/material";

import { AssignmentsCard } from "~ui/molecules/AssignmentsCard/AssignmentsCard";
import { EditableField } from "~ui/molecules/EditableField/EditableField";
import { LinkifiedEditableField } from "~ui/molecules/EditableField/LinkifiedEditableField";

const tAssignments = "Assignments";
const tBasicInformation = "Basic Information";
const tWebsite = "Website";
const tRegion = "Region";
const tSource = "Source";

interface OverviewTabProps {
  prospect: ProspectDto;
  onAssignToMe: () => Promise<void>;
  onUnassignMe: () => Promise<void>;
  onFieldUpdate: (field: "region" | "source" | "website", value: string) => Promise<void>;
  roles?: string[];
  onAssignUser?: (userId: string) => Promise<void>;
  onUnassignUser?: (userId: string) => Promise<void>;
}

export const OverviewTab = ({
  prospect,
  onAssignToMe,
  onUnassignMe,
  onFieldUpdate,
  roles = [],
  onAssignUser,
  onUnassignUser,
}: OverviewTabProps) => {
  return (
    <Box
      display="grid"
      gap={3}
      sx={{
        gridTemplateColumns: {
          md: "1fr 1fr", // Two equal columns on medium screens and up
          xs: "1fr", // Single column on small screens
        },
      }}
    >
      {/* Basic Info */}
      <Paper sx={{ p: 3 }} component="section" aria-labelledby="basic-info-heading" role="region">
        <Typography variant="h6" gutterBottom id="basic-info-heading">
          {tBasicInformation}
        </Typography>
        <Box display="grid" gap={2} component="div" role="group" aria-label="Basic information fields">
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {prospect.website ? (
                <Avatar
                  src={`https://www.google.com/s2/favicons?domain=${prospect.website}&sz=32`}
                  sx={{ height: 24, width: 24 }}
                  alt={`Favicon for ${prospect.website}`}
                >
                  <Language fontSize="small" color="primary" aria-hidden="true" />
                </Avatar>
              ) : (
                <Language fontSize="small" color="primary" aria-hidden="true" data-testid="language-icon" />
              )}
              <Typography variant="subtitle2" color="text.secondary">
                {tWebsite}
              </Typography>
            </Box>
            <LinkifiedEditableField value={prospect.website} onUpdate={(value) => onFieldUpdate("website", value)} />
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <LocationOn fontSize="small" sx={{ color: "info.main" }} aria-hidden="true" data-testid="location-icon" />
              <Typography variant="subtitle2" color="text.secondary">
                {tRegion}
              </Typography>
            </Box>
            <EditableField value={prospect.region} onUpdate={(value) => onFieldUpdate("region", value)} />
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Source fontSize="small" sx={{ color: "warning.main" }} aria-hidden="true" data-testid="source-icon" />
              <Typography variant="subtitle2" color="text.secondary">
                {tSource}
              </Typography>
            </Box>
            <EditableField value={prospect.source} onUpdate={(value) => onFieldUpdate("source", value)} />
          </Box>
        </Box>
      </Paper>

      {/* Assignments */}
      <Paper sx={{ p: 3 }} component="section" aria-labelledby="assignments-heading" role="region">
        <Typography variant="h6" gutterBottom id="assignments-heading">
          {tAssignments}
        </Typography>
        <AssignmentsCard
          prospect={prospect}
          onAssignToMe={onAssignToMe}
          onUnassignMe={onUnassignMe}
          roles={roles}
          onAssignUser={onAssignUser}
          onUnassignUser={onUnassignUser}
        />
      </Paper>
    </Box>
  );
};
